import React, { useState } from "react";
import PropTypes from "prop-types";
import { lighten, makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import Button from "@material-ui/core/Button";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import { useNavigate } from "react-router-dom";
import Alert from "@material-ui/lab/Alert";
import Snackbar from "@material-ui/core/Snackbar";
import { fetchWithToken } from "../../../../apiUtils";

function descendingComparator(a, b, orderBy) {
  if (a[orderBy] === null) {
    return 1;
  } else if (b[orderBy] === null) {
    return -1;
  }
  if (
    orderBy === "name" ||
    orderBy === "contactName" ||
    orderBy === "contactEmail" ||
    orderBy === "contactPhone"
  ) {
    return a[orderBy].toLowerCase().localeCompare(b[orderBy].toLowerCase());
  } else if (b[orderBy] < a[orderBy]) {
    return -1;
  } else if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  { id: "id", numeric: true, disablePadding: false, label: "ID" },
  { id: "name", numeric: true, disablePadding: false, label: "Name" },
  {
    id: "contactName",
    numeric: true,
    disablePadding: false,
    label: "Contact Name",
  },
  {
    id: "contactEmail",
    numeric: true,
    disablePadding: false,
    label: "Contact Email",
  },
  {
    id: "contactPhone",
    numeric: true,
    disablePadding: false,
    label: "Contact Phone",
  },
];

function EnhancedTableHead(props) {
  const { classes, order, orderBy, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align="center"
            padding="50px"
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <div class="input-group" style={{ justifyContent: "center" }}>
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : "asc"}
                onClick={createSortHandler(headCell.id)}
              >
                <div align="center" style={{ fontSize: ".9vw" }}>
                  {headCell.label}
                </div>
                {orderBy === headCell.id ? (
                  <span className={classes.visuallyHidden}>
                    {order === "desc"
                      ? "sorted descending"
                      : "sorted ascending"}
                  </span>
                ) : null}
              </TableSortLabel>
            </div>
          </TableCell>
        ))}
        <TableCell
          align="center"
          padding="50px"
          style={{ fontSize: "1vw", marginBottom: "25px" }}
        >
          Edit
        </TableCell>
        <TableCell
          align="center"
          padding="50px"
          style={{ fontSize: "1vw", marginBottom: "25px" }}
        >
          Delete
        </TableCell>
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  classes: PropTypes.object.isRequired,
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

const useToolbarStyles = makeStyles((theme) => ({
  root: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
  },
  highlight:
    theme.palette.type === "light"
      ? {
          color: theme.palette.secondary.main,
          backgroundColor: lighten(theme.palette.secondary.light, 0.85),
        }
      : {
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.secondary.dark,
        },
  title: {
    flex: "1 1 100%",
    margin: 40,
  },
  smallColumn: {
    width: "1px",
  },
}));

const EnhancedTableToolbar = (props) => {
  const classes = useToolbarStyles();

  return (
    <Toolbar className={classes.root}>
      <Typography
        className={classes.title}
        variant="h6"
        id="tableTitle"
        component="div"
      >
        Carrier Maintenance
      </Typography>
    </Toolbar>
  );
};

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  paper: {
    width: "100%",
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 750,
  },
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },
  smallColumn: {
    width: "10px",
  },
}));

export default function CarrierTable({ data, isAdmin }) {
  const history = useNavigate();
  const classes = useStyles();
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("id");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [rows, setRows] = useState(data);

  const handleRequestSort = (event, property) => {
    clearAlert();
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };
  let success = false;

  const handleDelete = (id) => {
    clearAlert();
    fetchWithToken(`api/Carriers/${id}`, "DELETE")
      .then((res) => {
        if (res.ok) success = true;
        return res.json();
      })
      .then((data) => {
        if (success) setRows(rows.filter((row) => row.id !== id));
        sendAlert(data.returnMessage, success ? "success" : "error");
      });
  };

  const [alertMessage, setAlertMessage] = useState({
    message: "",
    active: false,
  });

  const sendAlert = (message, varient) => {
    setAlertMessage({
      message: message,
      varient: varient,
      active: true,
    });
  };

  const clearAlert = () => {
    setAlertMessage({
      message: "",
      varient: "",
      active: false,
    });
  };

  const handleChangePage = (event, newPage) => {
    clearAlert();
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    clearAlert();
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyRows =
    rowsPerPage - Math.min(rowsPerPage, rows.length - page * rowsPerPage);

  return (
    <>
      <div className={classes.root}>
        <Snackbar
          className={classes.snack}
          open={alertMessage.active}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert severity={alertMessage.varient} onClose={() => clearAlert()}>
            {alertMessage.message}
          </Alert>
        </Snackbar>
        <Paper className={classes.paper}>
          <EnhancedTableToolbar />
          <TableContainer>
            <Table
              className={classes.table}
              aria-labelledby="tableTitle"
              size="medium"
              aria-label="enhanced table"
            >
              <EnhancedTableHead
                classes={classes}
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
                rowCount={rows.length}
              />
              <TableBody>
                {stableSort(rows, getComparator(order, orderBy))
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => {
                    return (
                      <>
                        <TableRow
                          hover
                          tabIndex={-1}
                          key={row.id}
                          style={{
                            height: "80px",
                            background: index % 2 ? "#f8f8f8" : "white",
                          }}
                        >
                          <TableCell align="center" style={{ width: "5%" }}>
                            {row.id}
                          </TableCell>
                          <TableCell align="center" style={{ width: "20%" }}>
                            {row.name}
                          </TableCell>
                          <TableCell align="center" style={{ width: "20%" }}>
                            {row.contactName}
                          </TableCell>
                          <TableCell align="left" style={{ width: "20%" }}>
                            {row.contactEmail}
                          </TableCell>
                          <TableCell align="left" style={{ width: "25%" }}>
                            {row.contactPhone}
                          </TableCell>
                          <TableCell align="center" style={{ width: "5%" }}>
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={() =>
                                history(`/carriers/edit/${row.id}`)
                              }
                              disabled={!isAdmin}
                            >
                              <EditIcon />
                            </Button>
                          </TableCell>
                          <TableCell
                            align="center"
                            style={{ width: "5%" }}
                            className={classes.smallColumn}
                          >
                            <Button
                              variant="contained"
                              color="secondary"
                              onClick={(e) => {
                                if (
                                  window.confirm(
                                    `Are you sure you wish to delete Carrier: ${row.name}?`
                                  )
                                )
                                  handleDelete(row.id);
                              }}
                              disabled={!isAdmin}
                            >
                              <DeleteIcon />
                            </Button>
                          </TableCell>
                        </TableRow>
                      </>
                    );
                  })}
                {emptyRows > 0 && rows.length >= rowsPerPage && (
                  <TableRow style={{ height: 80 * emptyRows }}>
                    <TableCell colSpan={7} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 30, 50]}
            component="div"
            count={rows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onChangeRowsPerPage={handleChangeRowsPerPage} // Material v < 4
            onRowsPerPageChange={handleChangeRowsPerPage} // Material v > 4
            onPageChange={handleChangePage} // Material v > 4
            onChangePage={handleChangePage} // Material v < 4
          />
        </Paper>
      </div>
    </>
  );
}
